import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SearchableCombobox from '@/components/ui/searchable-combobox';
import { ArrowLeft, Package, Send, CheckCircle, XCircle, Plus, Snowflake, Clock, CreditCard, Anchor, DollarSign, Trash2, FileText } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const estadoBadge = (estado) => {
  const map = {
    'Borrador': 'bg-slate-500/15 text-slate-600 dark:text-slate-400',
    'Enviada': 'bg-sky-500/15 text-sky-700 dark:text-sky-400',
    'Adjudicada': 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
    'En espera': 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
    'No adjudicada': 'bg-rose-500/15 text-rose-700 dark:text-rose-400',
  };
  return map[estado] || '';
};

const ofertaBadge = (estado) => {
  const map = {
    'Pendiente': 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
    'Aceptada': 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
    'Rechazada': 'bg-rose-500/15 text-rose-700 dark:text-rose-400',
  };
  return map[estado] || '';
};

const formatEmbarque = (fechaStr) => {
  if (!fechaStr) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(fechaStr)) {
    const parts = fechaStr.split('-');
    return new Date(parts[0], parts[1] - 1, parts[2]).toLocaleDateString('es-UY');
  }
  return fechaStr;
};

const DetallePublicacion = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pub, setPub] = useState(null);
  const [ofertas, setBids] = useState([]);
  const [compradores, setCompradores] = useState({});
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBidDialog, setShowBidDialog] = useState(false);
  const [showPdfDialog, setShowPdfDialog] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [allCompradores, setAllCompradores] = useState([]);
  const [newBidComprador, setNewBidComprador] = useState('');
  const [newBidMonto, setNewBidMonto] = useState('');
  const [savingBid, setSavingBid] = useState(false);

  useEffect(() => { loadAll(); }, [id]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [pubRes, ofertasRes, prodRes] = await Promise.all([
        axios.get(`${API}/publicaciones/${id}`),
        axios.get(`${API}/publicaciones/${id}/ofertas`),
        axios.get(`${API}/productos`),
      ]);
      setPub(pubRes.data);
      setBids(ofertasRes.data.ofertas || []);
      setCompradores(ofertasRes.data.compradores || {});
      setProductos(prodRes.data || []);
    } catch {
      toast.error('Error al cargar oferta');
    } finally {
      setLoading(false);
    }
  };

  const handleEstadoChange = async (nuevoEstado) => {
    try {
      await axios.put(`${API}/publicaciones/${id}`, { estadoPublicacion: nuevoEstado });
      toast.success(`Oferta marcada como ${nuevoEstado}`);
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleBidAction = async (ofertaId, estado) => {
    try {
      await axios.put(`${API}/ofertas/${ofertaId}`, { estado });
      toast.success(`Bid ${estado.toLowerCase()}`);
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const openManualBidDialog = async () => {
    try {
      const res = await axios.get(`${API}/compradores`);
      setAllCompradores(res.data);
    } catch { /* ignore */ }
    setNewBidComprador('');
    setNewBidMonto('');
    setShowBidDialog(true);
  };

  const handleDelete = async () => {
    if (window.confirm("¿Estás seguro de que querés eliminar esta oferta?")) {
      try {
        await axios.delete(`${API}/publicaciones/${id}`);
        toast.success("Oferta eliminada");
        navigate('/publicaciones');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error al eliminar');
      }
    }
  };

  const handleCreateBid = async () => {
    if (!newBidComprador || !newBidMonto) {
      toast.error('Completá todos los campos');
      return;
    }
    setSavingBid(true);
    try {
      await axios.post(`${API}/ofertas`, {
        publicacionId: id,
        compradorId: newBidComprador,
        monto: parseFloat(newBidMonto),
        esManual: true,
      });
      toast.success('Bid creado');
      setShowBidDialog(false);
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear bid');
    } finally {
      setSavingBid(false);
    }
  };

  if (loading || !pub) {
    return <div className="flex items-center justify-center min-h-[400px]"><div className="text-muted-foreground">Cargando...</div></div>;
  }

  const totalPrecio = (pub.fcls || []).reduce((sum, f) => sum + (f.precio || 0), 0);
  const totalPeso = (pub.fcls || []).reduce((sum, f) => sum + (f.peso || 0), 0);

  return (
    <div className="space-y-6 bg-background min-h-screen">
      {/* Header */}
      <div className="bg-background border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <Button variant="ghost" className="mb-4" onClick={() => navigate('/publicaciones')}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Volver
          </Button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg"><Snowflake className="h-6 w-6 text-primary" /></div>
              <div>
                <h1 className="text-2xl font-semibold tracking-tight">{pub.frigorificoNombre}</h1>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={estadoBadge(pub.estadoPublicacion)}>{pub.estadoPublicacion}</Badge>
                  <Badge variant="outline">{pub.tipo}</Badge>
                  <span className="text-sm text-muted-foreground">#{pub.plantaNumero}</span>
                </div>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" className="text-destructive hover:text-destructive hover:bg-destructive/10" onClick={handleDelete}>
                <Trash2 className="h-4 w-4 mr-2" /> Eliminar
              </Button>
              {pub.estadoPublicacion === 'Borrador' && (
                <>
                  <Button variant="outline" onClick={() => navigate(`/publicaciones/${id}/editar`)}>
                    Editar
                  </Button>
                  <Button onClick={() => handleEstadoChange('Enviada')}>
                    <Send className="h-4 w-4 mr-2" /> Publicar
                  </Button>
                </>
              )}
              {pub.estadoPublicacion === 'En espera' && (
                <>
                  <Button onClick={() => handleEstadoChange('Adjudicada')} className="bg-emerald-600 hover:bg-emerald-700">
                    <CheckCircle className="h-4 w-4 mr-2" /> Adjudicar
                  </Button>
                  <Button variant="outline" onClick={() => handleEstadoChange('Enviada')}>Volver a Enviada</Button>
                </>
              )}
              {pub.estadoPublicacion === 'Enviada' && (
                <Button variant="outline" onClick={() => handleEstadoChange('No adjudicada')}>No adjudicar</Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-6 lg:grid-cols-3 pb-12">
        {/* Main Info - 2 cols */}
        <div className="lg:col-span-2 space-y-6">
          {/* Publication Details */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4">Información de la Oferta</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <Snowflake className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <div><p className="text-xs text-muted-foreground">Frigorífico</p><p className="font-medium">{pub.frigorificoNombre}</p></div>
              </div>
              <div className="flex items-start gap-3">
                <Anchor className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <div><p className="text-xs text-muted-foreground">Planta</p><p className="font-medium">#{pub.plantaNumero}</p></div>
              </div>
              <div className="flex items-start gap-3">
                <CreditCard className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <div><p className="text-xs text-muted-foreground">Forma de Pago</p><p className="font-medium">{pub.paymentTerms || '—'}</p></div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <div><p className="text-xs text-muted-foreground">Estimado Embarque</p><p className="font-medium">{formatEmbarque(pub.estimadoEmbarque)}</p></div>
              </div>
            </div>
          </Card>

          {/* FCLs Table */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" /> FCLs ({pub.fcls?.length || 0})
            </h2>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead className="text-right">Peso</TableHead>
                  <TableHead className="text-right">Precio USD</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(pub.fcls || []).map((fcl, i) => (
                  <TableRow key={fcl.id || i}>
                    <TableCell className="font-mono text-xs">{i + 1}</TableCell>
                    <TableCell className="font-medium">
                      {fcl.descripcion}
                      {(() => {
                        const prod = productos.find(p => p.id === fcl.productoId);
                        if (prod && prod.ficha_tecnica_url) {
                          return (
                            <Button variant="ghost" size="sm" className="ml-2 h-6 px-2 text-primary" onClick={() => { setPdfUrl(BACKEND_URL + prod.ficha_tecnica_url); setShowPdfDialog(true); }}>
                              <FileText className="h-3 w-3 mr-1" /> Ficha
                            </Button>
                          );
                        }
                        return null;
                      })()}
                    </TableCell>
                    <TableCell className="text-right">{fcl.peso?.toLocaleString()} {fcl.unidadPeso}</TableCell>
                    <TableCell className="text-right font-medium">${fcl.precio?.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="border-t-2 font-semibold">
                  <TableCell colSpan={2} className="text-right">Total</TableCell>
                  <TableCell className="text-right">{totalPeso.toLocaleString()} kg</TableCell>
                  <TableCell className="text-right">USD {totalPrecio.toLocaleString()}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Card>
        </div>

        {/* Sidebar - Bids */}
        <div className="space-y-6">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-primary" /> Bids ({ofertas.length})
              </h2>
              {['Enviada', 'En espera'].includes(pub.estadoPublicacion) && (
                <Button variant="outline" size="sm" onClick={openManualBidDialog}>
                  <Plus className="h-4 w-4 mr-1" /> Manual
                </Button>
              )}
            </div>

            {ofertas.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No hay bids aún</p>
            ) : (
              <div className="space-y-3">
                {ofertas.map((oferta) => {
                  const comp = compradores[oferta.compradorId];
                  return (
                    <div key={oferta.id} className="border rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{comp?.nombre || oferta.compradorId}</p>
                          {comp?.empresa && <p className="text-xs text-muted-foreground">{comp.empresa}</p>}
                        </div>
                        <Badge className={ofertaBadge(oferta.estado)}>{oferta.estado}</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-lg font-bold">USD {oferta.monto?.toLocaleString()}</p>
                        <span className="text-xs text-muted-foreground">
                          {new Date(oferta.hora).toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      {oferta.estado === 'Pendiente' && pub.estadoPublicacion === 'Enviada' && (
                        <div className="flex gap-2 pt-1">
                          <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-8" onClick={() => handleBidAction(oferta.id, 'Aceptada')}>
                            <CheckCircle className="h-3 w-3 mr-1" /> Aceptar
                          </Button>
                          <Button size="sm" variant="outline" className="flex-1 h-8" onClick={() => handleBidAction(oferta.id, 'Rechazada')}>
                            <XCircle className="h-3 w-3 mr-1" /> Rechazar
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Meta */}
          <Card className="p-4">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Autor</span><span>{pub.autor}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Creada</span><span>{new Date(pub.createdAt).toLocaleDateString('es-UY')}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">ID</span><span className="font-mono text-xs">{pub.id}</span></div>
            </div>
          </Card>
        </div>
      </div>

      {/* Manual Bid Dialog */}
      <Dialog open={showBidDialog} onOpenChange={setShowBidDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Nueva Bid Manual</DialogTitle>
            <DialogDescription>Registrar un bid manualmente</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Comprador</Label>
              <SearchableCombobox
                value={newBidComprador}
                onSelect={setNewBidComprador}
                options={allCompradores.map(c => ({ value: c.id, label: `${c.nombre} ${c.empresa ? `— ${c.empresa}` : ''}` }))}
                placeholder="Seleccionar comprador..."
              />
            </div>
            <div className="space-y-2">
              <Label>Monto USD</Label>
              <Input type="number" value={newBidMonto} onChange={(e) => setNewBidMonto(e.target.value)} placeholder="Ej. 5000" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBidDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateBid} disabled={savingBid}>{savingBid ? 'Guardando...' : 'Crear Bid'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Dialog */}
      <Dialog open={showPdfDialog} onOpenChange={setShowPdfDialog}>
        <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Ficha Técnica</DialogTitle>
          </DialogHeader>
          <div className="flex-1 w-full bg-muted rounded-md overflow-hidden">
            {pdfUrl && (
              <iframe src={pdfUrl} className="w-full h-full" title="Ficha Técnica" />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DetallePublicacion;